import React, { memo } from "react";
import styles from "./add-request-technical-support-drawer-modal-without-machine.module.scss";
import { Title } from "@src/components/atoms/title/title";
import { DrawerIconContainer } from "@src/components/atoms/drawer-icon-container/drawer-icon-container";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import { useAddRequestTechnicalSupportDrawerModalWithoutMachine } from "./use-add-request-technical-support-drawer-modal-without-machine-hook";
import { MultipleDropAreaAddImage } from "@src/components/atoms/multiple-drop-area-add-image/multiple-drop-area-add-image";
import { useI18N } from "@src/language/language-context";
import AddRequestTechnicalSupport from "@src/assets/svg/add-request-technical-support-drawer-modal-icon.svg";
import SupportIcon from "@src/assets/svg/support-icon.svg";
import ArrowIcon from "@src/assets/svg/arrow.svg";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import { MAX_TICKET_TITLE_LENGTH } from "@src/cloud-connect/support/constants/constants";
import { MachineNameDropdown } from "@src/components/atoms/machine-name-dropdown/machine-name-dropdown";

const cssClassName =
  "add-request-technical-support-drawer-modal-without-machine";

export const AddRequestTechnicalSupportDrawerModalWithoutMachine = memo(() => {
  const {
    issueTitle,
    issueDetails,
    onChangeIssueTitle,
    onChangeIssueDetails,
    loadMachineListLoading,
    selectedMachine,
    machineOptions,
    onSelectMachine,
    handleSubmitRequestTechnicalSupport,
    isSubmitDisabled,
    isSubmitRequestTechnicalSupportWithoutMachineLoading,
    isSSEUnavailable,
    isDetailsExpanded,
    handleToggleDetails,
    images,
    addImages,
    removeImage,
  } = useAddRequestTechnicalSupportDrawerModalWithoutMachine();

  const i18n = useI18N();

  const texts = {
    sseUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
    title: i18n(
      `modules.addRequestTechnicalSupportDrawerModalWithoutMachine.title`,
    ),
    supportDetails: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.supportDetails",
    ),
    machine: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.machine",
    ),
    machinePlaceholder: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.machinePlaceholder",
    ),
    noMachineNames: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.noMachineNames",
    ),
    issueTitle: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.issueTitle",
    ),
    issueTitlePlaceholder: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.issueTitlePlaceholder",
    ),
    issueDetails: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.issueDetails",
    ),
    issueDetailsPlaceholder: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.issueDetailsPlaceholder",
    ),
    requestButton: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.requestButton",
    ),
    imageRemoveAlt: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.imageRemoveAlt",
    ),
    imageTooMany: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.imageTooMany",
    ),
    imageSizeTooLarge: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.imageSizeTooLarge",
    ),
    imageInvalidType: i18n(
      "modules.addRequestTechnicalSupportDrawerModalWithoutMachine.imageInvalidType",
    ),
  };

  return (
    <DrawerModal id="addRequestTechnicalSupportDrawerModalWithoutMachine">
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={24}>
          <Title tag="h2" text={texts.title} />
          <DrawerIconContainer icon={AddRequestTechnicalSupport} />

          <div className={styles[`${cssClassName}__content-wrapper`]}>
            <FlexRowContainer horizontalGap={6}>
              <img
                className={styles[`${cssClassName}__container-icon`]}
                alt={texts.supportDetails}
                src={SupportIcon}
              />
              <p className={styles[`${cssClassName}__subtitle`]}>
                {texts.supportDetails}
              </p>
              <IconButton
                icon={ArrowIcon}
                fun={handleToggleDetails}
                rotate={isDetailsExpanded}
              />
            </FlexRowContainer>

            {isDetailsExpanded && (
              <div className={styles[`${cssClassName}__details-box`]}>
                <div className={styles[`${cssClassName}__session-section`]}>
                  <label className={styles[`${cssClassName}__session-label`]}>
                    {texts.issueTitle}
                  </label>
                  <input
                    id="issueTitle"
                    type="text"
                    value={issueTitle}
                    onChange={(e) => onChangeIssueTitle(e.target.value)}
                    placeholder={texts.issueTitlePlaceholder}
                    className={styles[`${cssClassName}__input-and-textarea`]}
                    maxLength={MAX_TICKET_TITLE_LENGTH}
                  />
                </div>

                <div className={styles[`${cssClassName}__session-section`]}>
                  <label className={styles[`${cssClassName}__session-label`]}>
                    {texts.issueDetails}
                  </label>
                  <textarea
                    id="issueDetails"
                    value={issueDetails}
                    onChange={(e) => onChangeIssueDetails(e.target.value)}
                    placeholder={texts.issueDetailsPlaceholder}
                    className={styles[`${cssClassName}__input-and-textarea`]}
                  />
                </div>
                <div className={styles[`${cssClassName}__session-section`]}>
                  <MachineNameDropdown
                    options={machineOptions}
                    selectedMachine={selectedMachine}
                    onSelectMachine={onSelectMachine}
                    machineLabelText={texts.machine}
                    noMachineNamesText={texts.noMachineNames}
                    placeholderText={texts.machinePlaceholder}
                    isLoading={loadMachineListLoading}
                  />
                </div>
                <MultipleDropAreaAddImage
                  images={images}
                  onAdd={addImages}
                  onRemove={removeImage}
                  disabled={
                    isSubmitRequestTechnicalSupportWithoutMachineLoading
                  }
                  removeLabel={texts.imageRemoveAlt}
                  errorMessages={{
                    tooMany: texts.imageTooMany,
                    tooLarge: texts.imageSizeTooLarge,
                    invalid: texts.imageInvalidType,
                  }}
                />
                <div className={styles[`${cssClassName}__button-wrapper`]}>
                  <CustomTooltip
                    className={styles[`${cssClassName}__submit-tooltip`]}
                    content={
                      isSSEUnavailable ? texts.sseUnavailableTooltip : ""
                    }
                    position="top"
                  >
                    <ButtonWithLoader
                      fun={handleSubmitRequestTechnicalSupport}
                      text={texts.requestButton}
                      disabled={isSubmitDisabled}
                      loading={
                        isSubmitRequestTechnicalSupportWithoutMachineLoading
                      }
                      variant={["primary", "full-width"]}
                    />
                  </CustomTooltip>
                </div>
              </div>
            )}
          </div>
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
});

AddRequestTechnicalSupportDrawerModalWithoutMachine.displayName =
  "AddRequestTechnicalSupportDrawerModalWithoutMachine";
